import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAirbnbListings } from '../../../reducers/airbnb/selectors'
import { getSortedRoomsByPosition } from '../../../reducers/bookingCalendar/selectors/rooms'
import { Modal, Radio, RadioProps, withStyles } from '@material-ui/core'
import styled, { ITheme } from '../../../theme'
import { useTranslation } from 'react-i18next'
import Scrollbars from 'react-custom-scrollbars'
import mapRoomTypeToIcon from '../../../models/Room/mappers/mapRoomTypeToIcon'
import { green } from '@material-ui/core/colors'
import useSize from '../../../hooks/useSize'
import Button from '../../../components/Template/Button'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import airbnb from '../../../assets/integrations/airbnbminiicon.svg'
import { is } from '../../../components/MediaQuery'

const Wrapper = styled.div`
  min-width: 362px;
  width: 362px;
  max-height: 80vh;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #ebedef;
  stroke-width: 1;
  top: 50%;
  left: calc(50% - 224px);
  transform: translate(-50%, -50%);
  position: relative;
  outline: 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  @media (max-width: ${(props: ITheme) => props.theme.breakpoints.mobile}) {
    left: 50%;
    min-width: unset;
    width: calc(100% - 20px);
  }
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  align-items: center;
  justify-content: space-between;
`

export const RoomWrapperComp = ({
  icon,
  style = undefined,
  name,
  after = undefined,
  onClick = undefined,
  labelStyle = {},
}) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: '#ebedef',
      borderRadius: '8px',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
      cursor: onClick ? 'pointer' : 'default',
      ...style,
    }}
  >
    <img
      style={{
        width: '20px',
        height: '20px',
        marginRight: '15px',
      }}
      src={icon}
      alt={name}
    />
    <div
      style={{
        fontSize: '13px',
        color: '#696a6d',
        fontWeight: 400,
        ...labelStyle,
      }}
    >
      {name}
    </div>
    {after && after}
  </div>
)

function ConnectModal({
  open,
  listing,
  handleClose,
  connect,
  loading,
}: IConnectModal) {
  const listings = useSelector(getAirbnbListings)
  const rooms = useSelector(getSortedRoomsByPosition)
  const { t } = useTranslation()
  const [selectedRoom, setSelectedRoom] = useState(null)

  const size = useSize()
  const isDesktop = is.desktop()
  useEffect(() => {
    setSelectedRoom(null)
  }, [open])

  const filteredRooms = rooms.filter(
    r => !listings.some(l => Number(l.bedbooking_id_room) === Number(r.id)),
  )

  useEffect(() => {
    if (open && filteredRooms.length) {
      setSelectedRoom(filteredRooms[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Modal
      disablePortal={isDesktop}
      disableScrollLock={isDesktop}
      BackdropProps={{ invisible: isDesktop }}
      open={open}
      onClose={handleClose}
    >
      <Wrapper>
        <RoomWrapperComp
          labelStyle={{ fontWeight: 500 }}
          name={listing.name}
          icon={airbnb}
        />
        <Scrollbars
          autoHeightMax="max(120px, calc(80vh - 220px))"
          autoHeight={true}
          hideTracksWhenNotNeeded={false}
          style={{ backgroundColor: '#ebedef' }}
        >
          {filteredRooms.length > 0 ? (
            filteredRooms.map((r, i, arr) => {
              return (
                <RoomRow
                  key={i}
                  last={i === arr.length - 1}
                  selectedRoom={selectedRoom}
                  setSelectedRoom={setSelectedRoom}
                  room={r}
                />
              )
            })
          ) : (
            <div
              style={{
                textAlign: 'center',
                marginTop: '20px',
                padding: '5px',
                color: '#696a6d',
                fontSize: '13px',
              }}
            >
              {t('OUT_4831_no_room')}
            </div>
          )}
        </Scrollbars>

        <Actions>
          <Button
            onClick={loading ? undefined : connect(selectedRoom)}
            style={{
              width: '100%',
              fontWeight: 600,
              marginBottom: '20px',
              minHeight: '30px',
            }}
            disabled={selectedRoom === null}
            primary={true}
            size={size}
          >
            {loading ? <SpinningIcon /> : t('OUT_4831_connect_button')}
          </Button>
          <Button
            alight={true}
            onClick={handleClose}
            style={{
              width: '100%',
              fontWeight: 600,
              color: '#696a6d',
              minHeight: '30px',
            }}
            size={size}
          >
            {t('cancel')}
          </Button>
        </Actions>
      </Wrapper>
    </Modal>
  )
}

const GreenRadio = withStyles({
  root: {
    color: green[400],
    '&$checked': {
      color: green[600],
    },
  },
  checked: {},
})((props: RadioProps) => <Radio color="default" {...props} />)

const RoomRow = ({ room, selectedRoom, last, setSelectedRoom }) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRoom(event.target.value)
  }

  const toggleSelected = () => {
    setSelectedRoom(room.id)
  }

  return (
    <RoomWrapperComp
      icon={mapRoomTypeToIcon(room)}
      name={room.name}
      style={{ marginBottom: '0', paddingTop: '0', paddingBottom: '0' }}
      onClick={toggleSelected}
      after={
        <GreenRadio
          checked={String(selectedRoom) === String(room.id)}
          style={{ marginLeft: 'auto' }}
          onChange={handleChange}
          value={room.id}
        />
      }
    />
  )
}

export interface IConnectModal {
  open: boolean
  listing: any
  handleClose: () => void
  connect: (room: string) => () => void
  loading: boolean
}

export default ConnectModal
